import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link, useNavigate } from "react-router-dom"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { LcAddress } from "@/domains/core"
import { ScaleCard300 } from "@/domains/design/components/scale-card-300"
import { isPendingClassicLp } from "@/domains/pending-creation"

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 120_000

/**
 * Holding page shown after user successfully creates a Classic LP.
 * Polls until the pool appears in the API with a valid displayName, then clears liquidity filters and redirects to the liquidity list so the user can find their new pool.
 */
const LiquidityCreatingPage = observer(() => {
  const scaleApp = useScaleApplication()
  const navigate = useNavigate()
  const pending = scaleApp.PendingCreation.pendingClassicLp
  const [timedOut, setTimedOut] = useState(false)
  const startTimeRef = useRef<number>(Date.now())
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const navigatedToLiquidityRef = useRef(false)

  useEffect(() => {
    if (!pending || !isPendingClassicLp(pending)) {
      if (navigatedToLiquidityRef.current) return
      navigate("/liquidity/create", { replace: true })
      return
    }

    const checkPool = () => {
      scaleApp.MinimalPools.Orchestrator.forcePoolsSync()
      const pool = scaleApp.MinimalPools.Repository.find(new LcAddress(pending.pairAddress))
      const hasDisplayName = pool && (pool.displayName ?? "").trim().length > 0
      if (hasDisplayName) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        navigatedToLiquidityRef.current = true
        scaleApp.PendingCreation.clearPendingClassicLp()
        scaleApp.Pages.Liquidity.clearFiltersForNewPool()
        navigate("/liquidity", { replace: true })
      }
    }

    // Initial check after a short delay to allow first sync
    const initialTimeout = setTimeout(checkPool, 1500)

    pollIntervalRef.current = setInterval(checkPool, POLL_INTERVAL_MS)

    const timeoutId = setTimeout(() => {
      setTimedOut(true)
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }, TIMEOUT_MS)

    return () => {
      clearTimeout(initialTimeout)
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      clearTimeout(timeoutId)
    }
  }, [pending?.pairAddress, scaleApp, navigate])

  if (!pending || !isPendingClassicLp(pending)) {
    return null
  }

  const pairLabel = `${pending.token0Symbol}/${pending.token1Symbol}`

  return (
    <>
      <MetaData title="Creating pool" />
      <PageContainer>
        <PageTitle title="Your pair is being created" subTitle="The pool will appear in the app shortly." />
        <ScaleCard300 className="flex flex-col items-center justify-center gap-6 rounded-lg border border-p300-light p-8 text-center text-t100 sm:p-12">
          {timedOut ? (
            <>
              <p className="text-t200">
                This is taking longer than usual. You can go to Classic Liquidity and look for your pool, or try again
                later.
              </p>
              <Button variant="primary" asChild>
                <Link to="/liquidity">Go to Classic Liquidity</Link>
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary-500" aria-hidden />
              <p className="text-t200">
                Creating <span className="font-medium text-t100">{pairLabel}</span>…
              </p>
              <p className="text-sm text-t300">
                We are waiting for the pool to appear in the system. This usually takes under a minute.
              </p>
            </>
          )}
        </ScaleCard300>
      </PageContainer>
    </>
  )
})

export default LiquidityCreatingPage
